import type { InstrumentDraftQuestionType } from '@/features/instruments/infra/instrumentDraft.api'
import { resolveRuleDestination } from './routingRuleHelpers'
import type { BranchingRule, QuestionRoutingRules } from './types'

export const EXCLUSIVE_ROUTING_ANSWER_WARNING =
  'This answer includes routing logic. Respondents can select it with answers that follow the same path or have no routing, but not with answers tied to a different skip or branch.'

export const EXCLUSIVE_ROUTING_ANSWER_WARNING_SHORT =
  'This answer cannot be combined with answers that follow a different skip or branch.'

export type MultiSelectShape = Readonly<{
  type: InstrumentDraftQuestionType
  minSelections?: number | null
  maxSelections?: number | null
}>

/** Checkbox, or Multiple Choice whose min or max selections allow more than one answer. */
export function isMultiSelectQuestion(question: MultiSelectShape): boolean {
  if (question.type === 'checkbox') return true
  if (question.type !== 'multiple_choice') return false
  return (question.minSelections ?? 0) > 1 || (question.maxSelections ?? 0) > 1
}

function addRuleOptionIds(
  out: Set<string>,
  rules: readonly BranchingRule[] | undefined
): void {
  for (const rule of rules ?? []) {
    const id = rule.valueOptionId?.trim()
    if (id) out.add(id)
  }
}

/** Option ids referenced by any branching or skip rule on `questionId`. */
export function routingOptionIdsForQuestion(
  routingRules: readonly QuestionRoutingRules[],
  questionId: string
): Set<string> {
  const out = new Set<string>()
  for (const entry of routingRules) {
    if (entry.questionId !== questionId) continue
    addRuleOptionIds(out, entry.branchingRules)
    addRuleOptionIds(out, entry.skipRules)
  }
  return out
}

const EMPTY_CONFLICTING_OPTION_IDS: ReadonlySet<string> = new Set()

/**
 * Stable key for an equal-to rule that sends respondents somewhere specific.
 * Incomplete rules and non-equal operators do not create a path.
 */
export function routingDestinationKey(rule: BranchingRule): string | null {
  if (rule.operator !== 'is_equal_to') return null
  const dest = resolveRuleDestination(rule)
  if (!dest) return null
  if (dest.endOfInstrument === true) return 'end'
  const questionId = dest.destinationQuestionId?.trim()
  return questionId ? `q:${questionId}` : null
}

/**
 * Option ids on one question whose equal-to rules point at different destinations.
 * One path, or several options that share a path, is not a conflict.
 * Checkbox is not routing-eligible (BFP-251), so it never conflicts.
 */
export function conflictingRoutingOptionIds(
  question: MultiSelectShape,
  rules: readonly BranchingRule[],
): ReadonlySet<string> {
  if (question.type !== 'multiple_choice' || !isMultiSelectQuestion(question)) {
    return EMPTY_CONFLICTING_OPTION_IDS
  }

  const destinationByOption = new Map<string, string>()
  for (const rule of rules) {
    const optionId = rule.valueOptionId?.trim()
    const destination = routingDestinationKey(rule)
    if (!optionId || !destination || destinationByOption.has(optionId)) continue
    destinationByOption.set(optionId, destination)
  }

  if (new Set(destinationByOption.values()).size < 2) return EMPTY_CONFLICTING_OPTION_IDS
  return new Set(destinationByOption.keys())
}

function rulesIncludingDraft(
  rules: readonly BranchingRule[],
  rule: BranchingRule,
): readonly BranchingRule[] {
  const id = rule.id?.trim()
  if (!id) return [...rules, rule]
  return [...rules.filter((existing) => existing.id !== id), rule]
}

/**
 * True when this answer is part of a destination conflict on the same question.
 * A routing rule on its own, a shared path, and every other question stay quiet.
 */
export function isExclusiveRoutingRule(
  question: MultiSelectShape,
  rule: BranchingRule,
  rules: readonly BranchingRule[] = [],
): boolean {
  const optionId = rule.valueOptionId?.trim()
  if (!optionId) return false
  return conflictingRoutingOptionIds(question, rulesIncludingDraft(rules, rule)).has(optionId)
}
